<template>
  <div id="listWidget" class="mb-4">
    <div class="instruction-card card-item mb-4">
      <div class="instruction-header">
        <i class="fa-solid fa-link me-2 text-primary"></i>
        <span class="instruction-title">연결 방식을 선택하세요</span>
      </div>
      <div class="instruction-body">
        <div class="row g-3 justify-content-center">
          <div class="col-6">
            <button
              type="button"
              class="connection-mode-card connection-mode-card--thermostat"
              :class="{ 'is-active': registrationMode === 'thermostat' }"
              @click="registrationMode = 'thermostat'"
            >
              <i class="fa-solid fa-temperature-half connection-mode-card__icon connection-mode-card__icon--blue"></i>
              <div class="connection-mode-card__title connection-mode-card__title--blue">온도조절기</div>
              <div class="connection-mode-card__subtitle">온도 조절기를 통해 등록</div>
            </button>
          </div>
          <div class="col-6">
            <button
              type="button"
              class="connection-mode-card connection-mode-card--m11"
              :class="{ 'is-active': registrationMode === 'm11_m31' }"
              @click="registrationMode = 'm11_m31'"
            >
              <i class="fa-solid fa-microchip connection-mode-card__icon connection-mode-card__icon--green"></i>
              <div class="connection-mode-card__title connection-mode-card__title--green">M11 · M31 제어기</div>
              <div class="connection-mode-card__subtitle">지원 모델: M11, M31</div>
            </button>
          </div>
        </div>
      </div>
    </div>

    <template v-if="registrationMode === 'thermostat'">
    <!-- 단계별 안내 -->
    <div class="instruction-card card-item mb-3">
      <div class="instruction-header">
        <i class="fa-solid fa-list-ol me-2 text-primary"></i>
        <span class="instruction-title">전동커튼 확인</span>
      </div>
      <div class="instruction-body">
        <div class="instruction-step">
          <div class="step-number">1</div>
          <div class="step-content">
            <span>등록된 온도 조절기를 선택해주세요.</span>
          </div>
        </div>
        <div class="instruction-step">
          <div class="step-number">2</div>
          <div class="step-content">
            <span>등록된 온도 조절기가 없으면 온도 조절기를 먼저 등록해주세요.</span>
          </div>
        </div>
      </div>
    </div>

    <!-- <hr class="mb-3" /> -->

    <!-- <canvas id="canvas" /> -->

    <div class="row" v-if="tempControllers.length > 0">
      <div class="col-xl-6 mb-3">
        <div class="text-muted fs-12px mb-2"><b>온도 조절기 선택</b></div>
        <div class="list-group" style="border-radius: 1.2rem">
          <ListGroupItem style="border-radius: 1.2rem; margin-bottom: 4px;"
            v-for="(controller, index) in tempControllers" :key="controller._id">
            <div class="form-check">
              <input class="form-check-input thermostat-controller-check" type="checkbox" :id="`gridCheck${index}`" :value="controller._id"
                @change="onSelectControllerChange" />
              <label class="form-check-label" :for="`gridCheck${index}`">
                {{ formatTempControllerLabel(controller) }}
              </label>
            </div>
          </ListGroupItem>
        </div>
      </div>
    </div>

    <div class="row" v-else>
      <div class="col-xl-6 mb-3">
        <div class="list-group" style="border-radius: 1.2rem">
          <div class="list-group-item" style="border-radius: 1.2rem;">
            <label class="text-gray-800">
              등록된 온도 조절기가 없습니다.
            </label>
            <button type="button" class="btn btn-outline-primary mb-1 me-1" style="min-width: 100%;"
              @click="onRegistClick">
              온도 조절기 등록
            </button>
          </div>
        </div>
      </div>
    </div>

    <div class="row">
      <div class="col" />
      <div class="col" />
      <div class="col">
        <button type="button" class="btn btn-primary btn-lg" style="width: 100%" @click="onNextClick">
          다음
        </button>
      </div>
    </div>
    </template>

    <template v-else-if="registrationMode === 'm11_m31'">
      <div class="instruction-card card-item mb-3">
        <div class="instruction-header">
          <i class="fa-solid fa-list-ol me-2 text-primary"></i>
          <span class="instruction-title">전동커튼 확인</span>
        </div>
        <div class="instruction-body">
          <div class="instruction-step">
            <div class="step-number">1</div>
            <div class="step-content">
              <span>등록 버튼을 누르면 전동커튼 등록 대기 상태가 됩니다.</span>
            </div>
          </div>
          <div class="instruction-step">
            <div class="step-number">2</div>
            <div class="step-content">
              <span>전동모터의 셋팅 버튼을 길게 눌러 모터 LED가 세번 깜빡일 때 까지 누르고 있으면 자동으로 감지하여 등록합니다.</span>
            </div>
          </div>
          <div class="instruction-step">
            <div class="step-number">3</div>
            <div class="step-content">
              <span>등록된 M11, M31 제어기가 없으면 제어기를 먼저 등록해주세요.</span>
            </div>
          </div>
        </div>
      </div>
      <div class="row">
        <div class="col" />
        <div class="col" />
        <div class="col">
          <button type="button" class="btn btn-primary btn-lg" style="width: 100%" @click="onMotorCurtainRegistClick">
            등록
          </button>
        </div>
      </div>
    </template>
  </div>
</template>

<script setup>
import {
  ref,
  getCurrentInstance,
  computed,
  onMounted,
  onBeforeUnmount,
  watch,
  reactive,
} from 'vue';
import Axios, { DeviceApi } from '@/api';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
import useMobile from '@/common/hooks/useMobile';
import useMqttControl from '@/common/hooks/useMqttControl';
import ListGroupItem from '@/common/components/widgets/ListGroupItem.vue';
import { MQTTTempCommand, MQTTCurtainCommand, MQTTDooyaRonoAction, MQTTDooyaRonoCommand } from '@/common/constants';
import { useMQTT } from 'mqtt-vue-hook';

const props = defineProps(['handleNextStep']);
const mqttControl = reactive(useMqttControl());
const { emitter, mobile } = getCurrentInstance().appContext.config.globalProperties;
const store = useStore();
const router = useRouter();
const selectedController = ref('');
const mqttHook = useMQTT()
const registrationMode = ref('thermostat');
const motorCurtainRegistTimeoutId = ref(null);

const clearMotorCurtainRegistTimeout = () => {
  if (motorCurtainRegistTimeoutId.value != null) {
    clearTimeout(motorCurtainRegistTimeoutId.value);
    motorCurtainRegistTimeoutId.value = null;
    emitter.emit('hideLoading');
  }
};

const controllers = computed(() => {
  return store.getters['home/getCurrentZoneControllers'];
});
const zone = computed(() => store.getters['home/getCurrentZone']);
const dooyaSettingDevice = computed(() => store.state.home?.dooya?.settingDevice);
const tempControllers = computed(() => {
  return controllers.value.filter(item => item._id.split("_").length < 2);
});

const getControllerGroupNames = (controllerId) => {
  return (zone.value?.groups || [])
    .filter((group) =>
      group.devices?.some((device) => device.controllerId == controllerId),
    )
    .map((group) => group.name);
};

const formatTempControllerLabel = (controller) => {
  const model = String(controller?.model || '').trim();
  const serial = String(controller?._id || '').trim();
  const base =
    model && serial ? `${model} · ${serial}` : model || serial || '온도조절기';
  const groupNames = getControllerGroupNames(controller?._id);
  if (groupNames.length === 0) {
    return base;
  }
  return `${base} (${groupNames.join(', ')})`;
};

watch(dooyaSettingDevice, (next, prev) => {
  if (!prev && next) {
    clearMotorCurtainRegistTimeout();
    props.handleNextStep({
      serialCode: next.serial,
      mode: registrationMode.value
    });
  }
});

watch(registrationMode, (mode, prev) => {
  if (prev === 'thermostat' && mode !== 'thermostat') {
    selectedController.value = '';
    document.querySelectorAll('.thermostat-controller-check').forEach((input) => {
      input.checked = false;
    });
  }
  if (prev === 'm11_m31' && mode !== 'm11_m31') {
    clearMotorCurtainRegistTimeout();
  }
});

const onRegistClick = () => {
  router.replace("/settings/temp");
}

const onSelectControllerChange = (e) => {
  document.querySelectorAll('.thermostat-controller-check').forEach((input) => {
    input.checked = false;
  });
  e.target.checked = true;
  selectedController.value = e.target.value;
}

const onMotorCurtainRegistClick = () => {
  console.log("registrationMode.value", registrationMode.value);
  const hasM11OrM31 = controllers.value?.some((c) => c?.model === 'M11' || c?.model === 'M31');
  if (!hasM11OrM31) {
    emitter.emit('showPopAlert', {
      description: 'M11, M31 제어기가 등록되어 있지 않습니다.\n제어기를 먼저 등록해주세요.',
    });
    return;
  }

  clearMotorCurtainRegistTimeout();
  emitter.emit('showLoading');
  store.commit('home/setOnDooya', true);

  motorCurtainRegistTimeoutId.value = setTimeout(() => {
    motorCurtainRegistTimeoutId.value = null;
    emitter.emit('hideLoading');
    store.commit('home/clearDooya');
    
    emitter.emit('showPopAlert', {
      description: '전동커튼 등록에 실패했습니다.',
    });
  }, 30000);
};

onBeforeUnmount(() => {
  clearMotorCurtainRegistTimeout();
});

const onNextClick = () => {
  if (!selectedController.value?.length > 0) {
    emitter.emit('showPopAlert', {
      description: '온도조절기를 선택해주세요.',
    });
    return;
  }
  props.handleNextStep({
    serialCode: selectedController.value,
    mode: registrationMode.value
  });
  return;

  // checkCurtainCount(selectedController.value, () => {
  //   props.handleNextStep({
  //     serialCode: selectedController.value
  //   });
  // }, (message) => {
  //   emitter.emit('showPopAlert', {
  //     description: message,
  //   });
  // });
}

const checkCurtainCount = async (serial, handleSuccess, handleFailure) => {
  emitter.emit('showLoading');

  const testTopic = `/inshow/${serial}/${MQTTTempCommand.IsScanCount}`;
  let intervalCount = 0;

  mqttHook.publish("cmnd" + testTopic, "1", 0);
  const intervalId = setInterval(async () => {
    if (intervalCount >= 5) {
      clearInterval(intervalId);

      await mqttHook.unRegisterEvent("stat" + testTopic);
      await mqttHook.unSubscribe("stat" + testTopic, undefined);

      emitter.emit('hideLoading');

      if (handleFailure) {
        handleFailure("전동커튼 등록에 실패했습니다.");
      }
      return;
    }

    mqttHook.publish("cmnd" + testTopic, "1", 0);
    console.log("cmnd" + testTopic, "1")
    intervalCount++;
  }, 1000);

  mqttHook.subscribe(["stat" + testTopic], 0, undefined, async () => {
    await mqttHook.registerEvent("stat" + testTopic, async (topic, message) => {
      const value = parseInt(JSON.parse(message.toString())) || undefined;

      if (topic.includes(testTopic)) {
        clearInterval(intervalId);

        await mqttHook.unRegisterEvent(topic);
        await mqttHook.unSubscribe(topic, undefined);

        emitter.emit('hideLoading');

        if (value >= 1) {
          if (handleSuccess) {
            handleSuccess();
          }
        } else {
          if (handleFailure) {
            handleFailure("전동커튼을 포함하고 있지 않습니다.");
          }
        }
      }
    });
  });
}
</script>

<style scoped>
.instruction-card {
  background-color: #ffffff;
  border: 1px solid #e9ecef;
  border-radius: 1.2rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  overflow: hidden;
}

.instruction-header {
  display: flex;
  align-items: center;
  padding: 0.75rem 1rem;
  background-color: #f8f9fa;
  border-bottom: 1px solid #e9ecef;
}

.instruction-title {
  font-size: 0.9rem;
  font-weight: 600;
  color: #212529;
}

.instruction-body {
  padding: 0.75rem 1rem;
}

.instruction-step {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  margin-bottom: 0.75rem;
}

.instruction-step:last-child {
  margin-bottom: 0;
}

.step-number {
  width: 1.5rem;
  height: 1.5rem;
  min-width: 1.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #0d6efd;
  color: white;
  border-radius: 50%;
  font-weight: 600;
  font-size: 0.75rem;
}

.step-content {
  flex: 1;
  padding-top: 0.1rem;
  font-size: 0.85rem;
  line-height: 1.5;
  color: #495057;
}

.connection-mode-card {
  width: 100%;
  min-height: 138px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.45rem;
  padding: 1rem 0.35rem;
  border-radius: 1rem;
  border: 2px solid;
  background: #fff;
  cursor: pointer;
  transition: box-shadow 0.15s ease;
}

.connection-mode-card--thermostat {
  border-color: #0d6efd;
}

.connection-mode-card--m11 {
  border-color: #198754;
}

.connection-mode-card.is-active.connection-mode-card--thermostat {
  box-shadow: 0 0 0 3px rgba(13, 110, 253, 0.28);
}

.connection-mode-card.is-active.connection-mode-card--m11 {
  box-shadow: 0 0 0 3px rgba(25, 135, 84, 0.28);
}

.connection-mode-card__icon {
  font-size: 2rem;
  line-height: 1;
}

.connection-mode-card__icon--blue {
  color: #0d6efd;
}

.connection-mode-card__icon--green {
  color: #198754;
}

.connection-mode-card__title {
  font-size: 0.9rem;
  font-weight: 700;
  line-height: 1.25;
  text-align: center;
}

.connection-mode-card__title--blue {
  color: #0d6efd;
}

.connection-mode-card__title--green {
  color: #198754;
}

.connection-mode-card__subtitle {
  font-size: 0.7rem;
  color: #6c757d;
  line-height: 1.35;
  text-align: center;
  padding: 0 0.1rem;
}
</style>